import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable } from 'rxjs';

import { LoanStateDirector } from '../../../../shared/services/loan-state-director.interface';
import { BorrowerService } from '../../../../shared/services/borrower/borrower.service';
import { LoanApplicationsStates } from '../../../../shared/models';
import { PLApplicationProgressService } from './application-progress.service';
import { SessionService } from '../../../../shared/services/session/shared/session/session-service/session.service';
import { ROUTES } from '../../../../clpbp-app.settings';
import PersonalStateEnum = LoanApplicationsStates.PersonalStateEnum;
import { SessionScenario } from '../../../../shared/services/session/personal-loans/personal-loans-session/personal-loans-session.service';
import { PersonalLoanApplicationResponse } from '../../../../shared/models/index';
import { QueryParamService } from '@shared/services';
import LoanPurposeEnum = PersonalLoanApplicationResponse.LoanPurposeEnum;
import PersonalLoanTypeEnum = PersonalLoanApplicationResponse.LoanTypeEnum;

@Injectable()
export class LoanStateDirectorService implements LoanStateDirector<PersonalStateEnum> {
  constructor(
    private router: Router,
    private sessionService: SessionService,
    private borrowerService: BorrowerService,
    private personalLoansApplicationProgressService: PLApplicationProgressService,
    private queryParamService: QueryParamService) {}

  public directToReapply(loanAmount: number = undefined,
                         loanPurpose: LoanPurposeEnum = undefined,
                         mortgageBorrowerGuid = null,
                         loanType: PersonalLoanTypeEnum = PersonalLoanTypeEnum.Personal
                         ): void {
    this.personalLoansApplicationProgressService.markUnregistered();

    // Query params are case-sensitive, so mapping values to lowercase keys.
    this.redirect('register', {
      loanamount: loanAmount,
      loanpurpose: loanPurpose,
      mortgageborrowerguid: mortgageBorrowerGuid,
      loantype: loanType
    });
  }

  private directToAgentAssistedRegistration(): void {

    // TODO Once borrower/GetBorrowerLoanApplicationsStates is finished,
    // make that call after logging in to determine where to redirect.
    // For now, assuming a redirect to agent originated scenario if selecting this in dropdown.
    this.sessionService.personalLoanSession.data.sessionScenario = SessionScenario.AgentOriginated;

    this.redirect('register');
  }

  private directToSelectOffer(): void {
    this.personalLoansApplicationProgressService.markRegistered();
    this.redirect('select-offer');
  }

  private directToApply(): void {
    this.personalLoansApplicationProgressService.markRegistered();
    this.personalLoansApplicationProgressService.selectOffer();
    this.redirect('apply');
  }

  private directToSelfService(): void {
    this.redirect('to-dos');
  }

  private redirect(path: string, queryParams: any = undefined): void {
    this.router.navigate([ `/${ ROUTES.PL_FULL }/${ path }` ], { queryParams: queryParams });
  }

  private setPersonalLoanState(personalStateEnum): void {
    this.sessionService.personalLoanSession.data.loanState = personalStateEnum;
  }

  /**
   * Automatically directs a user to a route/step based on the current state of
   * the loan.
   */
  autoDirect(loanGuid?: string): Observable<LoanApplicationsStates.PersonalStateEnum> {
    const observer = this.borrowerService.getBorrowerPersonalStateEnum(loanGuid);

    observer.subscribe((personalStateEnum: LoanApplicationsStates.PersonalStateEnum) => this.directTo(personalStateEnum));
    return observer;
  }

  /**
   * Directs a user to a route/step based on a provided loan state argument.
   */
  directTo(personalStateEnum: LoanApplicationsStates.PersonalStateEnum): void {
    this.setPersonalLoanState(personalStateEnum);

    // TODO refactor application progress service to use PersonalStateEnum to track progression rather than section flags.
    switch (personalStateEnum) {
      case LoanApplicationsStates.PersonalStateEnum.InitialDisclosureConsent:
        this.directToAgentAssistedRegistration();
        break;

      case LoanApplicationsStates.PersonalStateEnum.New:
        this.directToSelectOffer();
        break;

      case LoanApplicationsStates.PersonalStateEnum.StateEmployment:
      case LoanApplicationsStates.PersonalStateEnum.LinkBankAccount:
      case LoanApplicationsStates.PersonalStateEnum.UploadAdditionalDocs:
      case LoanApplicationsStates.PersonalStateEnum.VerifyEmail:
      case LoanApplicationsStates.PersonalStateEnum.SubmissionRequired:
        this.directToApply();
        break;

      default:
        this.directToSelfService();
    }
  }
}
